import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, X, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { StockTakeFormData } from "@/pages/stock-take-create";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface ReviewStepProps {
  formData: StockTakeFormData;
  updateFormData: (updates: Partial<StockTakeFormData>) => void;
}

export function StockTakeReviewStep({ formData, updateFormData }: ReviewStepProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [varianceFilter, setVarianceFilter] = useState<"all" | "positive" | "negative" | "zero">(
    "all"
  );

  // Calculate item details
  const itemsWithDetails = useMemo(() => {
    return formData.selectedItems.map((item) => {
      const countedQty = formData.countedQuantities[item.code] || 0;
      const difference = countedQty - item.expectedQuantity;
      const valuePerItem = item.unitPrice;
      const totalValue = countedQty * valuePerItem;
      const needsRecount = formData.recountFlags[item.code] || false;

      return {
        ...item,
        countedQty,
        difference,
        valuePerItem,
        totalValue,
        needsRecount,
      };
    });
  }, [formData.selectedItems, formData.countedQuantities, formData.recountFlags]);

  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = itemsWithDetails;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      );
    }

    // Apply variance filter
    if (varianceFilter !== "all") {
      filtered = filtered.filter((item) => {
        if (varianceFilter === "positive") return item.difference > 0;
        if (varianceFilter === "negative") return item.difference < 0;
        if (varianceFilter === "zero") return item.difference === 0;
        return true;
      });
    }

    return filtered;
  }, [itemsWithDetails, searchQuery, varianceFilter]);

  // Calculate summary
  const summary = useMemo(() => {
    const totalItems = itemsWithDetails.length;
    const totalExpectedValue = itemsWithDetails.reduce(
      (sum, item) => sum + item.expectedQuantity * item.valuePerItem,
      0
    );
    const totalCountedValue = itemsWithDetails.reduce(
      (sum, item) => sum + item.totalValue,
      0
    );
    const totalVariance = totalCountedValue - totalExpectedValue;
    const itemsWithVariance = itemsWithDetails.filter((item) => item.difference !== 0).length;
    const itemsForRecount = itemsWithDetails.filter((item) => item.needsRecount).length;
    const positiveVariances = itemsWithDetails.filter((item) => item.difference > 0).length;
    const negativeVariances = itemsWithDetails.filter((item) => item.difference < 0).length;

    return {
      totalItems,
      totalExpectedValue,
      totalCountedValue,
      totalVariance,
      itemsWithVariance,
      itemsForRecount,
      positiveVariances,
      negativeVariances,
    };
  }, [itemsWithDetails]);

  // Toggle recount flag
  const toggleRecountFlag = (code: string) => {
    const newFlags = { ...formData.recountFlags };
    newFlags[code] = !newFlags[code];
    updateFormData({ recountFlags: newFlags });
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return `£${value.toFixed(2)}`;
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div>
        <h2 className="text-2xl font-semibold mb-1">Review Stock Take</h2>
        <p className="text-muted-foreground">
          Review the counted quantities and mark items for recount if needed
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalItems}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Expected Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.totalExpectedValue)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Counted Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.totalCountedValue)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Variance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                summary.totalVariance > 0
                  ? "text-green-600"
                  : summary.totalVariance < 0
                  ? "text-red-600"
                  : ""
              }`}
            >
              {summary.totalVariance > 0 ? "+" : ""}
              {formatCurrency(summary.totalVariance)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Variance Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Variance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Negative Variances</p>
                <p className="text-xl font-semibold">{summary.negativeVariances}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Positive Variances</p>
                <p className="text-xl font-semibold">{summary.positiveVariances}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="rounded-full bg-yellow-100 dark:bg-yellow-900/20 p-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Flagged for Recount</p>
                <p className="text-xl font-semibold">{summary.itemsForRecount}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant={varianceFilter === "all" ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setVarianceFilter("all")}
          >
            All ({itemsWithDetails.length})
          </Badge>
          <Badge
            variant={varianceFilter === "positive" ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setVarianceFilter("positive")}
          >
            Positive ({summary.positiveVariances})
          </Badge>
          <Badge
            variant={varianceFilter === "negative" ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setVarianceFilter("negative")}
          >
            Negative ({summary.negativeVariances})
          </Badge>
          <Badge
            variant={varianceFilter === "zero" ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setVarianceFilter("zero")}
          >
            No Variance
          </Badge>
        </div>
      </div>

      {/* Items Table */}
      <div className="rounded-lg border">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold text-right">Expected Qty</TableHead>
                <TableHead className="font-semibold text-right">Counted Qty</TableHead>
                <TableHead className="font-semibold text-right">Difference</TableHead>
                <TableHead className="font-semibold text-right">Value/Item</TableHead>
                <TableHead className="font-semibold text-right">Total Value</TableHead>
                <TableHead className="font-semibold text-center">Recount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, index) => (
                <TableRow
                  key={item.code}
                  className={`${index % 2 === 0 ? "bg-muted/30" : ""} ${
                    item.needsRecount ? "bg-yellow-50/50 dark:bg-yellow-900/10" : ""
                  }`}
                >
                  <TableCell className="font-mono font-medium">{item.code}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right font-mono">
                    {item.expectedQuantity}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {item.countedQty}
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={`font-mono font-semibold ${
                        item.difference > 0
                          ? "text-green-600"
                          : item.difference < 0
                          ? "text-red-600"
                          : ""
                      }`}
                    >
                      {item.difference > 0 ? "+" : ""}
                      {item.difference}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(item.valuePerItem)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {formatCurrency(item.totalValue)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={item.needsRecount}
                        onCheckedChange={() => toggleRecountFlag(item.code)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Recount Warning */}
      {summary.itemsForRecount > 0 && (
        <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {summary.itemsForRecount} item{summary.itemsForRecount !== 1 ? "s" : ""}{" "}
                flagged for recount
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                These items will need to be recounted before the stock take can be
                finalised. You can still complete the stock take and recount later.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
